// ==========================================================================
// screens/Journal.tsx — Isadora's journal (FF-053, 10 Oct 2026)
//   James: "Isadora Journal I would love please design it extra special for
//   caitlin to enjoy using see how fun you can make it."
//   A scrapbook, not a form: her name in handwriting on a soft cover in her own
//   colour, chapters by her age ("3 months old"), photos as polaroids with
//   handwritten captions, a sticker book of firsts to fill in, a little growth
//   chart, and letters to her for when she's older. Hearts so you can love
//   each other's moments. "On this day" brings back last month and last year.
//   10 Oct, round 2 (FF-060, James: "more fun like instagram with cropping and
//   some filters, making a cute album, stack of photos … get inspired from the
//   tree"): every new photo goes through the photo editor (crop, turn, looks);
//   pages with several photos are a little stack you can swipe; an Album tab
//   shows every photo; tapping a photo opens it full screen; soft watercolour
//   and leaves like the family tree.
// ==========================================================================
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parseISO, subMonths, subYears } from "date-fns";
import { Camera, Check, CheckSquare, ChevronLeft, ChevronRight, Download, Heart, ImagePlus, Images, Loader2, Mail, Pencil, Ruler, Star, Trash2, Wand2, X } from "lucide-react";
import { makeZip, saveBlob } from "@/lib/zip";
import PhotoEditor from "@/components/PhotoEditor";
import Sprig from "@/components/Sprig";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import { ErrorText } from "@/components/ui";
import { usePeople, useMe, type Person } from "@/lib/household";
import { tokenColour, ymd } from "@/lib/calendar";
import PhotoImg from "@/components/JournalPhoto";
import {
  FIRSTS, MOODS, ageOn, fetchPhoto, firstOf, monthOf, moodOf, photoFileName, takePendingPhotos, useArchiveEntry, useHeart, useJournal, useSaveEntry, useSetBornOn,
  type Entry, type EntryDraft, type EntryKind, type Photo,
} from "@/lib/journal";

import WoodlandArtwork from "@/components/WoodlandArtwork";
import JournalGrowthChart from "@/components/JournalGrowthChart";
import GrowthWall from "@/components/GrowthWall";
import "./Journal.css";

type Tab = "story" | "album" | "firsts" | "growing" | "letters";
type View = { ids: string[]; i: number; entries: Map<string, Entry> } | null;
const tilt = (id: string) => { let h = 0; for (const c of id) h = (h * 31 + c.charCodeAt(0)) | 0; return ((Math.abs(h) % 5) - 2) * 0.7; };
const ordinal = (n: number) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

export default function Journal() {
  const people = usePeople(); const journal = useJournal();
  const [params, setParams] = useSearchParams();
  const kids = (people.data ?? []).filter((p) => p.role === "child");
  const kid = kids.find((k) => k.id === params.get("who")) ?? kids[0];
  const [tab, setTab] = useState<Tab>("story");
  const [draft, setDraft] = useState<{ d: EntryDraft; files: File[] } | null>(null);
  const [view, setView] = useState<View>(null);

  // Photos handed over from the phone's Capture sheet.
  useEffect(() => {
    if (params.get("add") && kid) {
      const files = takePendingPhotos();
      setDraft({ d: blank(kid.id, "moment"), files });
      const p = new URLSearchParams(params); p.delete("add"); setParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, kid?.id]);

  if (people.isLoading) return <p className="text-sm text-ink-muted">Opening the journal…</p>;
  if (!kid) return <p className="text-ink-muted">Add a child on the Family screen to start a journal.</p>;

  const all = (journal.data?.entries ?? []).filter((e) => e.person_id === kid.id);
  const photos = journal.data?.photos ?? [];
  const colour = kid.colour;
  const start = (kind: EntryKind, first_key: string | null = null) => setDraft({ d: { ...blank(kid.id, kind), first_key }, files: [] });
  const open = (e: Entry) => setDraft({ d: { ...e }, files: [] });
  const firstsDone = all.filter((e) => e.kind === "first").length;
  const letters = all.filter((e) => e.kind === "letter");

  return (
    <div className="journal-book relative" style={{ ["--kid" as string]: `var(--${colour})` }}>
      {/* soft watercolour behind the whole journal, like the family tree */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 -top-6 bottom-0 -z-0 opacity-90" style={{ background: `radial-gradient(40% 30% at 90% 8%, ${tokenColour(colour, 0.1)}, transparent 70%), radial-gradient(35% 25% at 5% 35%, rgb(var(--tree-leaf) / 0.08), transparent 70%), radial-gradient(40% 30% at 80% 70%, ${tokenColour("chart-1", 0.06)}, transparent 70%)` }} />
      <div className="relative">
      <Cover kid={kid} entries={all.length} photos={photos.filter((p) => all.some((e) => e.id === p.entry_id)).length} firsts={firstsDone} />
      {kids.length > 1 && (
        <div className="mb-5 flex gap-2">{kids.map((k) => <button key={k.id} type="button" onClick={() => setParams({ who: k.id })} className={`rounded-full px-4 py-2 text-sm ${k.id === kid.id ? "font-semibold text-white" : "bg-surface-raised"}`} style={k.id === kid.id ? { background: tokenColour(k.colour) } : undefined}>{k.display_name}</button>)}</div>
      )}

      {/* Quick add */}
      <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {([["moment", "📸", "A moment", "A photo, a funny thing, a good day"], ["first", "⭐", "A first", "Add it to her book of firsts"], ["growth", "📏", "Growing", "Weight, length, head"], ["letter", "💌", "A letter", "For her to read one day"]] as const).map(([k, e, t, s]) => (
          <button key={k} type="button" onClick={() => start(k)} className="group flex flex-col items-start gap-1 rounded-2xl bg-surface p-4 text-left shadow-soft ring-1 ring-line/70 transition hover:-translate-y-0.5">
            <span className="text-3xl transition group-hover:scale-110">{e}</span>
            <span className="mt-1 font-semibold">{t}</span>
            <span className="hidden text-xs text-ink-muted sm:block">{s}</span>
          </button>
        ))}
      </div>

      <nav aria-label="Journal chapters" className="journal-chapters mb-7 flex flex-wrap gap-2">
        {([["story", "Her story"], ["album", "Album"], ["firsts", `Firsts ${firstsDone}/${FIRSTS.length}`], ["growing", "Growing"], ["letters", `Letters${letters.length ? ` ${letters.length}` : ""}`]] as const).map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} aria-pressed={tab === k} className={`rounded-full px-4 py-2 text-sm transition ${tab === k ? "font-semibold text-white" : "text-ink-muted hover:text-ink"}`} style={tab === k ? { background: tokenColour(colour) } : undefined}>{l}</button>
        ))}
      </nav>

      {journal.isLoading && <p className="text-sm text-ink-muted">Opening the journal…</p>}
      {journal.isError && <p role="alert" className="mb-5 rounded-xl border border-line bg-surface p-4">The journal couldn’t be loaded. Please reload to try again.</p>}
      {tab === "story" && !journal.isError && <GrowthWall kid={kid} entries={all} onOpen={open} />}
      {tab === "story" && !journal.isError && <Story kid={kid} entries={all} photos={photos} onOpen={open} onStart={start} onView={setView} />}
      {tab === "album" && !journal.isError && <Album kid={kid} entries={all} photos={photos} onView={setView} onStart={() => start("moment")} />}
      {tab === "firsts" && !journal.isError && <Firsts kid={kid} entries={all} photos={photos} onOpen={open} onStart={start} />}
      {tab === "growing" && !journal.isError && <Growing kid={kid} entries={all} onStart={() => start("growth")} onOpen={open} />}
      {tab === "letters" && !journal.isError && <Letters kid={kid} entries={letters} onOpen={open} onStart={() => start("letter")} />}
      <div className="journal-woodland-endpaper" aria-hidden="true">
        <WoodlandArtwork />
        <div><span>Small adventures. Wonderful company.</span><p>Every little day is a story worth keeping.</p></div>
      </div>

      {draft && <EntrySheet kid={kid} draft={draft.d} initialFiles={draft.files} taken={all.filter((e) => e.kind === "first" && e.id !== draft.d.id).map((e) => e.first_key!)} photos={photos.filter((p) => p.entry_id === draft.d.id)} onClose={() => setDraft(null)} />}
      {view && <Lightbox kid={kid} view={view} photos={photos} onClose={() => setView(null)} onEdit={(e) => { setView(null); open(e); }} />}
      </div>
    </div>
  );
}

const blank = (person_id: string, kind: EntryKind): EntryDraft => ({ person_id, kind, happened_on: ymd(new Date()), first_key: null, title: null, body: null, mood: null, weight_g: null, length_mm: null, head_mm: null });

// --------------------------------------------------------------------------
function Cover({ kid, entries, photos, firsts }: { kid: Person; entries: number; photos: number; firsts: number }) {
  const setBorn = useSetBornOn();
  const [born, setBornText] = useState("");
  const age = ageOn(kid.born_on, ymd(new Date()));
  const c = kid.colour;
  return (
    <div className="journal-cover">
      <WoodlandArtwork className="journal-cover-art" />
      <div className="journal-cover-copy relative">
        <div className="font-hand text-2xl text-ink-muted">Once upon a little adventure…</div>
        <h1 className="font-hand text-[68px] font-bold leading-[0.9] sm:text-[96px]" style={{ color: tokenColour(c) }}>{kid.display_name}</h1>
        {kid.born_on ? (
          <p className="mt-3 text-[15px] text-ink">
            <b>{age}</b> old today. Born {format(parseISO(kid.born_on), "EEEE d MMMM yyyy")}.
          </p>
        ) : (
          <form className="mt-4 flex max-w-md flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (born) setBorn.mutate({ personId: kid.id, born_on: born }); }}>
            <span className="w-full text-[15px] text-ink">When was {kid.display_name} born? It lets every page say how old she was.</span>
            <input type="date" value={born} onChange={(e) => setBornText(e.target.value)} className={`${inputCls} w-auto bg-surface`} />
            <button type="submit" disabled={!born || setBorn.isPending} className="rounded-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50" style={{ background: tokenColour(c) }}>Save</button>
          </form>
        )}
        <div className="mt-6 flex flex-wrap gap-x-7 gap-y-2 text-sm text-ink-muted">
          <span><b className="num text-lg text-ink">{entries}</b> moments</span>
          <span><b className="num text-lg text-ink">{photos}</b> photos</span>
          <span><b className="num text-lg text-ink">{firsts}</b> of {FIRSTS.length} firsts</span>
        </div>
      </div>
      <span className="journal-cover-keepsake">a little sunshine, a lot of love</span>
    </div>
  );
}

// --------------------------------------------------------------------------
function Story({ kid, entries, photos, onOpen, onStart, onView }: { kid: Person; entries: Entry[]; photos: Photo[]; onOpen: (e: Entry) => void; onStart: (k: EntryKind) => void; onView: (v: View) => void }) {
  const byId = useMemo(() => new Map(entries.map((e) => [e.id, e])), [entries]);
  const viewEntry = (e: Entry, i: number) => onView({ ids: photos.filter((p) => p.entry_id === e.id && p.uploaded).sort((a, b) => a.sort - b.sort).map((p) => p.id), i, entries: byId });
  const today = ymd(new Date());
  const born = kid.born_on;
  const memories = useMemo(() => {
    const lm = ymd(subMonths(new Date(), 1)); const ly = ymd(subYears(new Date(), 1));
    return entries.filter((e) => e.happened_on === lm || e.happened_on === ly);
  }, [entries]);
  const chapters = useMemo(() => {
    const m = new Map<string, Entry[]>();
    const sorted = entries.filter((x) => x.kind !== "letter").sort((a, b) => b.happened_on.localeCompare(a.happened_on) || b.created_at.localeCompare(a.created_at));
    for (const e of sorted) {
      const k = born ? String(monthOf(born, e.happened_on)) : e.happened_on.slice(0, 7);
      m.set(k, [...(m.get(k) ?? []), e]);
    }
    return [...m.entries()].sort((a, b) => (born ? Number(b[0]) - Number(a[0]) : b[0].localeCompare(a[0])));
  }, [entries, born]);

  if (!entries.filter((e) => e.kind !== "letter").length) {
    return (
      <div className="rounded-3xl border-2 border-dashed border-line px-6 py-14 text-center">
        <div className="text-5xl">📖</div>
        <p className="mt-3 font-hand text-4xl" style={{ color: tokenColour(kid.colour) }}>Her story starts here</p>
        <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted">Add a photo from today, the first time she smiled, or what she weighed at the last check-up. Little things count most.</p>
        <button type="button" onClick={() => onStart("moment")} className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-white" style={{ background: tokenColour(kid.colour) }}><Camera className="h-4 w-4" />Add the first page</button>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {memories.length > 0 && (
        <div className="rounded-2xl p-5 ring-1" style={{ background: tokenColour("chart-6", 0.1), borderColor: tokenColour("chart-6", 0.3) }}>
          <div className="mb-3 font-hand text-3xl text-ink">On this day</div>
          <div className="grid items-start gap-5 sm:grid-cols-2 xl:grid-cols-3">{memories.map((e) => <Page key={e.id} e={e} kid={kid} photos={photos} onOpen={() => onOpen(e)} onView={(i) => viewEntry(e, i)} flat label={e.happened_on === ymd(subYears(new Date(), 1)) ? "One year ago today" : "One month ago today"} />)}</div>
        </div>
      )}
      {chapters.map(([k, list]) => {
        const first = list[list.length - 1].happened_on; const last = list[0].happened_on;
        const n = Number(k);
        const head = born ? (n === 0 ? "Her first month" : `${n} month${n === 1 ? "" : "s"} old`) : format(parseISO(`${k}-01`), "MMMM yyyy");
        return (
          <section key={k}>
            <div className="journal-chapter-heading mb-5 flex items-baseline gap-4">
              <Sprig className="h-9 w-9 shrink-0 self-center" />
              <h2 className="font-hand text-[44px] font-bold leading-none" style={{ color: tokenColour(kid.colour) }}>{head}</h2>
              <span className="text-sm text-ink-muted">{born && n < 24 ? `${ordinal(n + 1)} month. ` : ""}{format(parseISO(first), "d MMM")}{first !== last ? ` to ${format(parseISO(last), "d MMM yyyy")}` : format(parseISO(first), " yyyy")}</span>
              <span className="h-px flex-1 bg-line" />
            </div>
            <div className="columns-1 gap-6 sm:columns-2 xl:columns-3 [&>*]:mb-6">
              {list.map((e) => <Page key={e.id} e={e} kid={kid} photos={photos} onOpen={() => onOpen(e)} onView={(i) => viewEntry(e, i)} />)}
            </div>
          </section>
        );
      })}
      {entries.some((e) => e.happened_on > today) && <p className="text-center text-xs text-ink-muted">Some pages are dated in the future.</p>}
    </div>
  );
}

function Hearts({ e }: { e: Entry }) {
  const heart = useHeart(); const me = useMe(); const people = usePeople();
  const mine = !!me && e.hearts.includes(me.id);
  const names = e.hearts.map((h) => people.data?.find((p) => p.id === h)?.display_name).filter(Boolean).join(" and ");
  return (
    <button type="button" onClick={(ev) => { ev.stopPropagation(); heart.mutate(e); }} aria-label={mine ? "Unlove" : "Love this"} title={names ? `Loved by ${names}` : "Love this"}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs transition ${mine ? "text-chart-1" : "text-ink-muted hover:text-chart-1"}`}>
      <Heart className={`h-4 w-4 transition ${mine ? "scale-110 fill-current" : ""}`} />{e.hearts.length > 0 && <span className="num">{e.hearts.length}</span>}
    </button>
  );
}

/** natural: show the whole photo at its own shape (10 Oct, James: photos from the
 *  library were being cut off by the square frame). While loading it holds a 4:5 space. */

/** One scrapbook page: a polaroid when there's a photo, a note card when there isn't. */
function Page({ e, kid, photos, onOpen, onView, flat, label }: { e: Entry; kid: Person; photos: Photo[]; onOpen: () => void; onView: (i: number) => void; flat?: boolean; label?: string }) {
  const mine = photos.filter((p) => p.entry_id === e.id && p.uploaded).sort((a, b) => a.sort - b.sort);
  const age = ageOn(kid.born_on, e.happened_on);
  const f = firstOf(e.first_key); const mood = moodOf(e.mood);
  const words = e.title || e.body;
  const meta = <div className="flex items-center gap-2 text-xs text-ink-muted"><span>{label ?? format(parseISO(e.happened_on), "EEE d MMM yyyy")}{age && !label ? `, ${age}` : ""}</span><span className="ml-auto" /><Hearts e={e} /></div>;

  if (e.kind === "growth") {
    return (
      <div className="block w-full break-inside-avoid rounded-2xl bg-surface p-4 text-left shadow-soft ring-1 ring-line/70"><button type="button" onClick={onOpen} className="block w-full text-left">
        <div className="mb-2 flex items-center gap-2 font-semibold"><span className="text-xl">📏</span>Growing</div>
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          {e.weight_g && <span><b className="num text-2xl">{(e.weight_g / 1000).toFixed(2)}</b> <span className="text-sm text-ink-muted">kg</span></span>}
          {e.length_mm && <span><b className="num text-2xl">{(e.length_mm / 10).toFixed(1)}</b> <span className="text-sm text-ink-muted">cm long</span></span>}
          {e.head_mm && <span><b className="num text-2xl">{(e.head_mm / 10).toFixed(1)}</b> <span className="text-sm text-ink-muted">cm head</span></span>}
        </div>
        {e.body && <p className="mt-2 text-sm text-ink-muted">{e.body}</p>}
        </button><div className="mt-3">{meta}</div>
      </div>
    );
  }

  if (mine.length) {
    const many = mine.length > 1;
    return (
      <div className="relative block w-full break-inside-avoid pt-3 text-left" style={flat ? undefined : { transform: `rotate(${tilt(e.id)}deg)` }}>
        {/* the rest of the stack peeking out behind */}
        {many && mine.slice(1, 3).map((p, i) => (
          <div key={p.id} aria-hidden className="absolute inset-x-2 bottom-6 top-5 rounded-sm bg-white p-2.5 shadow-[0_8px_20px_-8px_rgba(40,20,30,0.3)] ring-1 ring-black/5"
            style={{ transform: `rotate(${i ? -5 : 4}deg) translate(${i ? -10 : 12}px, ${i ? 8 : 4}px)` }}>
            <PhotoImg id={p.id} className="h-full w-full rounded-[2px] object-cover opacity-90" />
          </div>
        ))}
        <div className="relative rounded-sm bg-white p-3 pb-4 shadow-[0_10px_25px_-8px_rgba(40,20,30,0.25),0_2px_6px_-2px_rgba(40,20,30,0.15)] ring-1 ring-black/5">
          {/* washi tape */}
          <span aria-hidden className="absolute -top-3 left-1/2 z-10 h-6 w-24 -translate-x-1/2 -rotate-3 rounded-[2px] opacity-80" style={{ background: `repeating-linear-gradient(45deg, ${tokenColour(kid.colour, 0.45)} 0 6px, ${tokenColour(kid.colour, 0.3)} 6px 12px)` }} />
          {f && <span className="absolute -right-3 -top-3 z-10 flex h-14 w-14 rotate-12 items-center justify-center rounded-full text-3xl shadow-md" style={{ background: tokenColour("chart-6") }} title={f.label}>{f.emoji}</span>}
          <Swipe ids={mine.map((p) => p.id)} onView={onView} />
          <button type="button" onClick={onOpen} className="block w-full px-1 pt-3 text-left text-[#2a2023]">
            {f && <div className="text-xs font-semibold uppercase tracking-wide text-[#8a6a1a]">{f.label}</div>}
            {words && <p className="font-hand text-[26px] leading-[1.1]">{e.title ?? e.body}{mood && <span className="ml-1.5 text-xl">{mood.emoji}</span>}</p>}
            {e.title && e.body && <p className="mt-1 text-sm text-[#5a4a50]">{e.body}</p>}
          </button>
          <div className="mt-2 px-1 [&_*]:!text-[#7a6a70]">{meta}</div>
          <button type="button" onClick={onOpen} aria-label="Edit page" className="absolute bottom-3 right-12 rounded-full p-1.5 text-[#9a8a90] hover:bg-black/5 hover:text-[#2a2023]"><Pencil className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    );
  }

  if (f) {
    return (
      <div className="block w-full break-inside-avoid rounded-2xl p-5 text-left shadow-soft ring-1" style={{ background: tokenColour("chart-6", 0.12), borderColor: tokenColour("chart-6", 0.35) }}><button type="button" onClick={onOpen} className="block w-full text-left">
        <div className="flex items-center gap-3">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-surface text-3xl shadow-sm">{f.emoji}</span>
          <div className="min-w-0"><div className="text-xs font-semibold uppercase tracking-wide text-warn">A first</div><div className="font-hand text-3xl leading-none">{f.label}</div></div>
        </div>
        {(e.title || e.body) && <p className="mt-3 text-[15px]">{e.title}{e.title && e.body ? ". " : ""}{e.body}</p>}
        </button><div className="mt-3">{meta}</div>
      </div>
    );
  }

  return (
    <div className="block w-full break-inside-avoid rounded-2xl p-5 text-left shadow-soft ring-1 ring-line/70 transition hover:-translate-y-0.5"
      style={{ background: `repeating-linear-gradient(to bottom, transparent 0 31px, ${tokenColour(kid.colour, 0.18)} 31px 32px), rgb(var(--surface))`, transform: flat ? undefined : `rotate(${tilt(e.id) / 2}deg)` }}><button type="button" onClick={onOpen} className="block w-full text-left">
      {mood && <span className="float-right ml-2 text-3xl" title={mood.label}>{mood.emoji}</span>}
      {e.title && <p className="font-hand text-[30px] font-bold leading-8">{e.title}</p>}
      {e.body && <p className="whitespace-pre-line font-hand text-[24px] leading-8 text-ink">{e.body}</p>}
      </button><div className="mt-2">{meta}</div>
    </div>
  );
}

// --------------------------------------------------------------------------
function Firsts({ kid, entries, photos, onOpen, onStart }: { kid: Person; entries: Entry[]; photos: Photo[]; onOpen: (e: Entry) => void; onStart: (k: EntryKind, key: string) => void }) {
  const byKey = new Map(entries.filter((e) => e.kind === "first").map((e) => [e.first_key!, e]));
  const done = byKey.size;
  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <p className="font-hand text-4xl">{done === 0 ? "Her book of firsts" : done === FIRSTS.length ? "Every sticker collected!" : `${done} down, ${FIRSTS.length - done} to go`}</p>
        <div className="h-3 min-w-[160px] flex-1 overflow-hidden rounded-full bg-surface-raised"><div className="h-full rounded-full transition-all" style={{ width: `${(done / FIRSTS.length) * 100}%`, background: tokenColour("chart-6") }} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {FIRSTS.map((f) => {
          const e = byKey.get(f.key);
          const ph = e ? photos.find((p) => p.entry_id === e.id && p.uploaded) : undefined;
          return e ? (
            <button key={f.key} type="button" onClick={() => onOpen(e)} className="relative flex flex-col items-center rounded-2xl bg-surface p-4 text-center shadow-soft ring-2 transition hover:-translate-y-0.5" style={{ borderColor: tokenColour("chart-6"), ["--tw-ring-color" as string]: tokenColour("chart-6", 0.6) }}>
              {ph ? <PhotoImg id={ph.id} className="h-20 w-20 rounded-full object-cover ring-4 ring-surface" /> : <span className="flex h-20 w-20 items-center justify-center rounded-full text-4xl" style={{ background: tokenColour("chart-6", 0.18) }}>{f.emoji}</span>}
              {ph && <span className="absolute right-3 top-3 text-2xl">{f.emoji}</span>}
              <div className="mt-3 font-semibold leading-tight">{f.label}</div>
              <div className="mt-1 font-hand text-xl leading-none" style={{ color: tokenColour(kid.colour) }}>{ageOn(kid.born_on, e.happened_on) ?? format(parseISO(e.happened_on), "d MMM yyyy")}</div>
              <div className="text-xs text-ink-muted">{format(parseISO(e.happened_on), "d MMM yyyy")}</div>
            </button>
          ) : (
            <button key={f.key} type="button" onClick={() => onStart("first", f.key)} className="group flex flex-col items-center rounded-2xl border-2 border-dashed border-line p-4 text-center transition hover:border-solid hover:bg-surface">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-surface-raised text-4xl opacity-40 grayscale transition group-hover:opacity-90 group-hover:grayscale-0">{f.emoji}</span>
              <div className="mt-3 font-semibold leading-tight text-ink-muted group-hover:text-ink">{f.label}</div>
              <div className="mt-1 text-xs text-ink-muted">{f.around}</div>
            </button>
          );
        })}
      </div>
      <p className="mt-6 text-center text-xs text-ink-muted">The ages are only a rough guide. Every baby does things in her own time.</p>
    </div>
  );
}

// --------------------------------------------------------------------------
function Growing({ kid, entries, onStart, onOpen }: { kid: Person; entries: Entry[]; onStart: () => void; onOpen: (e: Entry) => void }) {
  const g = entries.filter((e) => e.kind === "growth" || e.weight_g || e.length_mm || e.head_mm).sort((a, b) => a.happened_on.localeCompare(b.happened_on));
  const last = (k: "weight_g" | "length_mm" | "head_mm") => [...g].reverse().find((e) => e[k]);
  const prev = (k: "weight_g" | "length_mm" | "head_mm") => [...g].reverse().filter((e) => e[k])[1];
  const tiles: { k: "weight_g" | "length_mm" | "head_mm"; label: string; unit: string; f: (n: number) => string }[] = [
    { k: "weight_g", label: "Weight", unit: "kg", f: (n) => (n / 1000).toFixed(2) },
    { k: "length_mm", label: "Length", unit: "cm", f: (n) => (n / 10).toFixed(1) },
    { k: "head_mm", label: "Head", unit: "cm", f: (n) => (n / 10).toFixed(1) },
  ];
  if (!g.length) {
    return (
      <div className="rounded-3xl border-2 border-dashed border-line px-6 py-14 text-center">
        <div className="text-5xl">📏</div>
        <p className="mt-3 font-hand text-4xl">Watch her grow</p>
        <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted">Pop in her weight and length after each check-up and a chart builds itself here.</p>
        <button type="button" onClick={onStart} className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-white" style={{ background: tokenColour(kid.colour) }}><Ruler className="h-4 w-4" />Add a measurement</button>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        {tiles.map((t) => { const l = last(t.k); const p = prev(t.k); return (
          <div key={t.k} className="rounded-2xl bg-surface p-5 shadow-soft ring-1 ring-line/70">
            <div className="text-sm text-ink-muted">{t.label}</div>
            <div className="mt-1"><b className="num text-3xl">{l ? t.f(l[t.k]!) : "–"}</b> <span className="text-ink-muted">{l ? t.unit : ""}</span></div>
            {l && p && <div className="mt-1 text-sm text-good">{l[t.k]! >= p[t.k]! ? "+" : ""}{t.f(l[t.k]! - p[t.k]!)} {t.unit} since {format(parseISO(p.happened_on), "d MMM")}</div>}
            {l && <div className="text-xs text-ink-muted">{format(parseISO(l.happened_on), "d MMM yyyy")}</div>}
          </div>
        ); })}
      </div>
      <JournalGrowthChart key={kid.id} kid={kid} points={g.filter((e) => e.weight_g).map((e) => ({ on: e.happened_on, v: e.weight_g! / 1000 }))} />
      <div className="flex justify-end"><button type="button" onClick={onStart} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ background: tokenColour(kid.colour) }}><Ruler className="h-4 w-4" />Add a measurement</button></div>
      <ul className="overflow-hidden rounded-2xl bg-surface ring-1 ring-line/70">
        {[...g].reverse().map((e) => (
          <li key={e.id} className="border-b border-line/60 last:border-0">
            <button type="button" onClick={() => onOpen(e)} className="flex w-full flex-wrap items-center gap-x-6 gap-y-1 px-5 py-3 text-left hover:bg-surface-raised/60">
              <span className="w-36 text-sm"><b>{format(parseISO(e.happened_on), "d MMM yyyy")}</b><br /><span className="text-xs text-ink-muted">{ageOn(kid.born_on, e.happened_on)}</span></span>
              <span className="num text-sm">{e.weight_g ? `${(e.weight_g / 1000).toFixed(2)} kg` : ""}</span>
              <span className="num text-sm">{e.length_mm ? `${(e.length_mm / 10).toFixed(1)} cm long` : ""}</span>
              <span className="num text-sm">{e.head_mm ? `${(e.head_mm / 10).toFixed(1)} cm head` : ""}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --------------------------------------------------------------------------
function Letters({ kid, entries, onOpen, onStart }: { kid: Person; entries: Entry[]; onOpen: (e: Entry) => void; onStart: () => void }) {
  const people = usePeople();
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-lg text-sm text-ink-muted">Letters for {kid.display_name} to read when she's older: what she's like right now, what you hope for her, the funny things she'll never remember.</p>
        <button type="button" onClick={onStart} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ background: tokenColour(kid.colour) }}><Mail className="h-4 w-4" />Write a letter</button>
      </div>
      {entries.length === 0 && <div className="rounded-3xl border-2 border-dashed border-line px-6 py-14 text-center"><div className="text-5xl">💌</div><p className="mt-3 font-hand text-4xl">No letters yet</p></div>}
      {entries.map((e) => (
        <button key={e.id} type="button" onClick={() => onOpen(e)} className="relative block w-full overflow-hidden rounded-2xl bg-surface p-7 text-left shadow-soft ring-1 ring-line/70 sm:p-10"
          style={{ backgroundImage: `linear-gradient(135deg, ${tokenColour(kid.colour, 0.07)}, transparent 40%)` }}>
          <span className="absolute right-6 top-6 text-3xl">💌</span>
          <div className="text-xs text-ink-muted">{format(parseISO(e.happened_on), "EEEE d MMMM yyyy")}{ageOn(kid.born_on, e.happened_on) ? `. You were ${ageOn(kid.born_on, e.happened_on)} old.` : ""}</div>
          <p className="mt-4 font-hand text-[34px] leading-none">Dear {kid.display_name},</p>
          {e.title && <p className="mt-2 font-semibold">{e.title}</p>}
          <p className="mt-3 line-clamp-[12] whitespace-pre-line font-hand text-[25px] leading-[1.35] text-ink">{e.body}</p>
          <p className="mt-5 font-hand text-[28px]" style={{ color: tokenColour(kid.colour) }}>Love, {people.data?.find((p) => p.id === e.created_by)?.display_name ?? "us"} <Heart className="inline h-5 w-5 fill-current" /></p>
          <div className="mt-2"><Hearts e={e} /></div>
        </button>
      ))}
    </div>
  );
}

/** One or more photos you can swipe through (or tap the arrows on a computer). Tap a photo to open it big. */
function Swipe({ ids, onView }: { ids: string[]; onView: (i: number) => void }) {
  const row = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(0);
  const go = (i: number) => { const el = row.current; if (!el) return; el.scrollTo({ left: el.clientWidth * i, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" }); };
  if (ids.length === 1) return <button type="button" onClick={() => onView(0)} className="block w-full"><PhotoImg natural id={ids[0]} className="block h-auto max-h-[36rem] w-full rounded-[2px] bg-[#f4f1ec] object-contain" /></button>;
  return (
    <div className="group relative">
      <div ref={row} onScroll={(e) => { const el = e.currentTarget; setAt(Math.round(el.scrollLeft / Math.max(1, el.clientWidth))); }}
        className="flex snap-x snap-mandatory overflow-x-auto rounded-[2px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {ids.map((id, i) => (
          <button key={id} type="button" onClick={() => onView(i)} className="w-full shrink-0 snap-center">
            <PhotoImg id={id} className="aspect-[4/5] w-full bg-[#f4f1ec] object-cover" />
          </button>
        ))}
      </div>
      <span className="absolute right-2 top-2 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-semibold text-white">{at + 1}/{ids.length}</span>
      {at > 0 && <button type="button" onClick={() => go(at - 1)} aria-label="Previous photo" className="absolute left-1.5 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/85 p-1.5 text-[#2a2023] shadow group-hover:block"><ChevronLeft className="h-4 w-4" /></button>}
      {at < ids.length - 1 && <button type="button" onClick={() => go(at + 1)} aria-label="Next photo" className="absolute right-1.5 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/85 p-1.5 text-[#2a2023] shadow group-hover:block"><ChevronRight className="h-4 w-4" /></button>}
      <div className="mt-2 flex justify-center gap-1.5">{ids.map((id, i) => <span key={id} className={`h-1.5 rounded-full transition-all ${i === at ? "w-4 bg-[#2a2023]/70" : "w-1.5 bg-[#2a2023]/25"}`} />)}</div>
    </div>
  );
}

/** Download one photo, or several as a zip. */
async function downloadPhotos(kid: Person, picks: { id: string; happened: string; n: number }[], setBusy: (b: boolean) => void) {
  setBusy(true);
  try {
    if (picks.length === 1) {
      const blob = await fetchPhoto(picks[0].id);
      saveBlob(blob, photoFileName(kid.display_name, picks[0].happened, picks[0].n, blob.type));
    } else {
      const files = [];
      for (const p of picks) { const blob = await fetchPhoto(p.id); files.push({ name: photoFileName(kid.display_name, p.happened, p.n, blob.type), blob, date: parseISO(p.happened) }); }
      saveBlob(await makeZip(files), `${kid.display_name.toLowerCase()}-photos-${ymd(new Date())}.zip`);
    }
  } finally { setBusy(false); }
}

/** A photo ribbon, with a native range control for touch and keyboard access. */
function Timeline({ kid, items, at, onJump, children }: { kid: Person; items: { id: string; happened: string }[]; at: number; onJump: (i: number) => void; children?: ReactNode }) {
  const [drag,setDrag]=useState<number|null>(null);
  const n=items.length;
  if(n<2) return <div className="mb-4">{children}</div>;
  const i=Math.min(n-1,Math.max(0,drag??at));const cur=items[i];
  const samples=Array.from(new Set(Array.from({length:Math.min(n,9)},(_,j)=>Math.round(j*(n-1)/(Math.min(n,9)-1)))));
  const commit=()=>{if(drag!==null){onJump(drag);setDrag(null);}};
  return <div className="journal-timeline">
    <div className="journal-timeline-heading"><span className="journal-eyebrow">Follow the little path</span><span>{ageOn(kid.born_on,cur.happened)??format(parseISO(cur.happened),'d MMM yyyy')}</span></div>
    <div className="journal-photo-ribbon" aria-hidden="true">{samples.map(j=><div key={items[j].id} className={j===i?'is-current':''}><PhotoImg id={items[j].id}/></div>)}</div>
    <div className="journal-ribbon-control">
      <input type="range" min={0} max={n-1} step={1} value={i} aria-label="Travel through photo memories" aria-valuetext={format(parseISO(cur.happened),'d MMM yyyy')+', '+(i+1)+' of '+n+' photos'}
        onChange={e=>setDrag(Number(e.target.value))} onPointerUp={commit} onKeyUp={commit} onBlur={commit} onPointerCancel={()=>setDrag(null)}/>
      <span className="journal-ribbon-date">{format(parseISO(cur.happened),'d MMM yyyy')} · {i+1} / {n}</span>
    </div>
    <div className="journal-ribbon-labels"><span>{format(parseISO(items[0].happened),'MMM yyyy')}</span><span>Drag or use arrow keys</span><span>{format(parseISO(items[n-1].happened),'MMM yyyy')}</span></div>
    {children}
  </div>;
}

/** Every photo, oldest first so the timeline reads left to right, grouped by month of age. */
function Album({ kid, entries, photos, onView, onStart }: { kid: Person; entries: Entry[]; photos: Photo[]; onView: (v: View) => void; onStart: () => void }) {
  const byId = useMemo(() => new Map(entries.map((e) => [e.id, e])), [entries]);
  const list = useMemo(() => photos.filter((p) => p.uploaded && byId.has(p.entry_id))
    .sort((a, b) => byId.get(a.entry_id)!.happened_on.localeCompare(byId.get(b.entry_id)!.happened_on) || a.sort - b.sort), [photos, byId]);
  const ids = list.map((p) => p.id);
  const items = useMemo(() => list.map((p) => ({ id: p.id, happened: byId.get(p.entry_id)!.happened_on })), [list, byId]);
  const groups = useMemo(() => {
    const m = new Map<string, Photo[]>();
    for (const p of list) {
      const e = byId.get(p.entry_id)!;
      const k = kid.born_on ? String(monthOf(kid.born_on, e.happened_on)) : e.happened_on.slice(0, 7);
      m.set(k, [...(m.get(k) ?? []), p]);
    }
    return [...m];
  }, [list, byId, kid.born_on]);
  const [at, setAt] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const refs = useRef(new Map<string, HTMLElement>());
  // which photo is in view, for the timeline thumb
  useEffect(() => {
    let raf = 0;
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => {
      const top = 140; let best = 0; let bestD = Infinity;
      ids.forEach((id, j) => { const el = refs.current.get(id); if (!el) return; const d = Math.abs(el.getBoundingClientRect().top - top); if (d < bestD) { bestD = d; best = j; } });
      setAt(best);
    }); };
    window.addEventListener("scroll", onScroll, { passive: true }); return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [ids]);
  const jump = (j: number) => {
    const el = refs.current.get(ids[j]); if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 150, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    setFlash(ids[j]); setTimeout(() => setFlash(null), 1400); setAt(j);
  };
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const download = () => {
    const picks = list.filter((p) => picked.has(p.id)).map((p) => ({ id: p.id, happened: byId.get(p.entry_id)!.happened_on, n: p.sort + 1 }));
    void downloadPhotos(kid, picks, setBusy).then(() => { setSelecting(false); setPicked(new Set()); });
  };
  if (!list.length) return (
    <div className="rounded-3xl border-2 border-dashed border-line px-6 py-14 text-center">
      <Images className="mx-auto h-12 w-12 text-ink-muted" />
      <p className="mt-3 font-hand text-4xl" style={{ color: tokenColour(kid.colour) }}>No photos yet</p>
      <button type="button" onClick={onStart} className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-white" style={{ background: tokenColour(kid.colour) }}><Camera className="h-4 w-4" />Add a moment</button>
    </div>
  );
  return (
    <div className="storybook-album">
      <div className="storybook-intro">
        <div><p className="journal-eyebrow">A keepsake for our family · private album</p><h2>The little days.<br/>The very big memories.</h2><p>A wander through {kid.display_name}’s story, one small adventure at a time.</p><div className="storybook-jumps"><button type="button" onClick={()=>jump(0)}>The first page <ChevronRight size={16}/></button><button type="button" onClick={()=>jump(list.length-1)}>The latest chapter <ChevronRight size={16}/></button></div></div>
        <div className="storybook-postcard"><PhotoImg id={list[0].id} className="aspect-[4/3] w-full object-cover"/><span>{format(parseISO(items[0].happened),'d MMMM yyyy')} · where our story begins</span></div>
      </div>
      <Timeline kid={kid} items={items} at={at} onJump={jump}>
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-line pt-3">
          <p className="text-sm text-ink-muted">{list.length} photo{list.length === 1 ? "" : "s"}{selecting && picked.size ? ` · ${picked.size} picked` : ""}</p>
          <div className="flex items-center gap-2">
            {selecting && picked.size > 0 && <button type="button" onClick={download} disabled={busy} className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: tokenColour(kid.colour) }}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}{picked.size === 1 ? "Download" : `Download ${picked.size} as a zip`}</button>}
            <button type="button" onClick={() => { setSelecting((v) => !v); setPicked(new Set()); }} className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ring-1 ring-line/70 ${selecting ? "bg-ink text-surface" : "bg-surface text-ink"}`}>{selecting ? <><X className="h-4 w-4" />Done</> : <><CheckSquare className="h-4 w-4" />Select</>}</button>
          </div>
        </div>
      </Timeline>
      <div className="space-y-8">
      {groups.map(([k, ps], chapter) => {
        const n = Number(k);
        const head = kid.born_on ? (n === 0 ? "Her first month" : `${n} month${n === 1 ? "" : "s"} old`) : format(parseISO(`${k}-01`), "MMMM yyyy");
        return (
          <section key={k} className="storybook-chapter">
            <div className="storybook-chapter-title"><span className="storybook-number">{String(chapter+1).padStart(2,'0')}</span><div><p className="journal-eyebrow">Chapter {chapter+1}</p><h2>{head}</h2></div><span className="text-sm text-ink-muted">{ps.length} photo{ps.length === 1 ? "" : "s"}</span></div>
            <div className="storybook-photos">
              {ps.map((p) => {
                const on = picked.has(p.id);
                return (
                  <button key={p.id} type="button" ref={(el) => { if (el) refs.current.set(p.id, el); else refs.current.delete(p.id); }}
                    aria-label={`${selecting?'Select':'Open'} ${p.caption || byId.get(p.entry_id)!.title || 'photo'}, ${format(parseISO(byId.get(p.entry_id)!.happened_on),'d MMM yyyy')}`} aria-pressed={selecting ? on : undefined}
                    onClick={() => selecting ? toggle(p.id) : onView({ ids, i: ids.indexOf(p.id), entries: byId })}
                    className={`group relative overflow-hidden rounded-lg transition ${flash === p.id ? "ring-4 ring-offset-2 ring-offset-bg" : ""} ${on ? "ring-4" : ""}`} style={flash === p.id || on ? { ["--tw-ring-color" as string]: tokenColour(kid.colour) } : undefined}>
                    <PhotoImg id={p.id} className={`aspect-square w-full object-cover transition ${selecting && !on ? "opacity-70" : ""} group-hover:scale-105`} />
                    <span className="storybook-photo-caption"><strong>{p.caption || byId.get(p.entry_id)!.title || 'A little moment to keep'}</strong><small>{format(parseISO(byId.get(p.entry_id)!.happened_on),'d MMM yyyy')}</small></span>
                    {selecting && <span className={`absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white ${on ? "text-white" : "bg-black/30"}`} style={on ? { background: tokenColour(kid.colour) } : undefined}>{on && <Check className="h-3.5 w-3.5" />}</span>}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
      </div>
    </div>
  );
}

/** A photo full screen: swipe or arrows, the caption and how old she was underneath. */
function Lightbox({ kid, view, photos, onClose, onEdit }: { kid: Person; view: NonNullable<View>; photos: Photo[]; onClose: () => void; onEdit: (e: Entry) => void }) {
  const [i, setI] = useState(view.i);
  const [busy, setBusy] = useState(false);
  const row = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.querySelector<HTMLButtonElement>('button[aria-label="Close"]')?.focus();
    return () => { document.body.style.overflow = overflow; previous?.focus(); };
  }, []);
  useEffect(() => { const el = row.current; if (el) el.scrollLeft = el.clientWidth * view.i; }, [view.i]);
  useEffect(() => {
    const k = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose(); if (ev.key === "ArrowRight") go(i + 1); if (ev.key === "ArrowLeft") go(i - 1); };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  });
  const go = (n: number) => { const el = row.current; if (!el || n < 0 || n >= view.ids.length) return; el.scrollTo({ left: el.clientWidth * n, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" }); };
  const photo = photos.find((p) => p.id === view.ids[i]);
  const e = photo ? view.entries.get(photo.entry_id) : undefined;
  const age = e ? ageOn(kid.born_on, e.happened_on) : null;
  return (
    <div ref={dialog} className="fixed inset-0 z-50 flex flex-col bg-[#14100f] text-white" role="dialog" aria-modal="true" aria-label="Photo" onKeyDown={event=>{
      if(event.key!=='Tab')return;
      const buttons=Array.from(dialog.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')??[]).filter(button=>button.getClientRects().length>0);
      const first=buttons[0],last=buttons[buttons.length-1];
      if(event.shiftKey && document.activeElement===first){event.preventDefault();last?.focus();}
      else if(!event.shiftKey && document.activeElement===last){event.preventDefault();first?.focus();}
    }}>
      <div className="flex items-center justify-between px-4 pb-2 pt-[calc(env(safe-area-inset-top)+12px)]">
        <span className="text-sm text-white/70">{i + 1} of {view.ids.length}</span>
        <div className="flex items-center gap-1">
          {photo && e && <button type="button" disabled={busy} onClick={() => void downloadPhotos(kid, [{ id: photo.id, happened: e.happened_on, n: photo.sort + 1 }], setBusy)} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-white/80 hover:bg-white/10 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Save</button>}
          {e && <button type="button" onClick={() => onEdit(e)} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-white/80 hover:bg-white/10"><Pencil className="h-4 w-4" />Edit page</button>}
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-2 hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>
      </div>
      <div ref={row} onScroll={(ev) => { const el = ev.currentTarget; setI(Math.round(el.scrollLeft / Math.max(1, el.clientWidth))); }}
        className="flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {view.ids.map((id, index) => (
          <div key={id} className="flex h-full w-full shrink-0 snap-center items-center justify-center p-3">
            {index === i && <PhotoImg original id={id} className="h-full w-full rounded-md object-contain" />}
          </div>
        ))}
      </div>
      <div className="relative px-6 pb-[calc(env(safe-area-inset-bottom)+18px)] pt-3 text-center">
        {e?.title && <p className="font-hand text-3xl leading-tight">{e.title}</p>}
        {e && <p className="mt-1 text-xs text-white/60">{format(parseISO(e.happened_on), "EEEE d MMMM yyyy")}{age ? `, ${age}` : ""}</p>}
        {i > 0 && <button type="button" onClick={() => go(i - 1)} aria-label="Previous" className="absolute left-3 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/10 p-2 hover:bg-white/20 sm:block"><ChevronLeft className="h-5 w-5" /></button>}
        {i < view.ids.length - 1 && <button type="button" onClick={() => go(i + 1)} aria-label="Next" className="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/10 p-2 hover:bg-white/20 sm:block"><ChevronRight className="h-5 w-5" /></button>}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
function EntrySheet({ kid, draft, initialFiles, taken, photos, onClose }: { kid: Person; draft: EntryDraft; initialFiles: File[]; taken: string[]; photos: Photo[]; onClose: () => void }) {
  const save = useSaveEntry(); const archive = useArchiveEntry();
  const [d, setD] = useState(draft); const set = (p: Partial<EntryDraft>) => setD({ ...d, ...p });
  // Each new photo keeps its original so it can be edited again from scratch (FF-060).
  const [items, setItems] = useState<{ orig: File; file: File; edited: boolean }[]>(() => initialFiles.map((f) => ({ orig: f, file: f, edited: false })));
  const [queue, setQueue] = useState<number[]>(() => initialFiles.map((_, i) => i));
  const files = useMemo(() => items.map((x) => x.file), [items]);
  const editing = queue[0];
  const [batch, setBatch] = useState(initialFiles.length);
  const nextInQueue = () => setQueue((q) => q.slice(1));
  const [kg, setKg] = useState(d.weight_g ? (d.weight_g / 1000).toString() : ""); const [cm, setCm] = useState(d.length_mm ? (d.length_mm / 10).toString() : ""); const [head, setHead] = useState(d.head_mm ? (d.head_mm / 10).toString() : "");
  const [err, setErr] = useState<string | null>(null); const [sure, setSure] = useState(false);
  const pick = useRef<HTMLInputElement>(null);
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);
  const age = ageOn(kid.born_on, d.happened_on);
  const c = tokenColour(kid.colour);
  const num = (s: string, k: number) => { const n = Number(s.replace(",", ".")); return s.trim() && Number.isFinite(n) && n > 0 ? Math.round(n * k) : null; };

  const submit = () => {
    const row: EntryDraft = { ...d, weight_g: num(kg, 1000), length_mm: num(cm, 10), head_mm: num(head, 10) };
    if (row.kind === "first" && !row.first_key) { setErr("Which first?"); return; }
    if (row.kind === "growth" && !row.weight_g && !row.length_mm && !row.head_mm) { setErr("Add at least one measurement."); return; }
    if (row.kind !== "growth" && row.kind !== "first" && !row.title?.trim() && !row.body?.trim() && !files.length && !photos.length) { setErr("Add a few words or a photo."); return; }
    if (row.kind === "moment" && !row.title?.trim() && !row.body?.trim() && files.length) row.title = null;
    if (row.kind === "moment" && !row.title?.trim() && !row.body?.trim()) row.body = "📸";
    save.mutate({ d: row, files }, { onSuccess: onClose, onError: (e) => setErr(e.message) });
  };
  const titleFor = { moment: "A moment", first: "A first", growth: "Growing", letter: `A letter to ${kid.display_name}` }[d.kind];

  return (
    <>
    {editing !== undefined && items[editing] && (
      <PhotoEditor file={items[editing].orig} index={Math.max(0, batch - queue.length)} total={Math.max(1, batch)} accent={c}
        onDone={(f) => { setItems(items.map((x, j) => (j === editing ? { ...x, file: f, edited: true } : x))); nextInQueue(); }}
        onSkip={() => { setItems(items.map((x, j) => (j === editing ? { ...x, file: x.orig, edited: false } : x))); nextInQueue(); }}
        onCancel={() => setQueue([])} />
    )}
    <Sheet title={d.id ? "Edit page" : titleFor} onClose={onClose}>
      <div className="space-y-5">
        {!d.id && (
          <div className="grid grid-cols-4 gap-1.5 rounded-2xl bg-surface-raised p-1.5">
            {([["moment", "📸", "Moment"], ["first", "⭐", "First"], ["growth", "📏", "Growing"], ["letter", "💌", "Letter"]] as const).map(([k, e, l]) => (
              <button key={k} type="button" onClick={() => set({ kind: k })} className={`flex flex-col items-center rounded-xl py-2 text-xs ${d.kind === k ? "bg-surface font-semibold shadow-sm" : "text-ink-muted"}`}><span className="text-xl">{e}</span>{l}</button>
            ))}
          </div>
        )}

        <Lbl label="When" hint={age ? `${kid.display_name} was ${age === "The day she was born" ? "born that day" : `${age} old`}` : undefined}>
          <input type="date" className={inputCls} value={d.happened_on} onChange={(e) => set({ happened_on: e.target.value })} />
        </Lbl>

        {d.kind === "first" && (
          <div>
            <span className="text-sm font-medium text-ink-muted">Which first</span>
            <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {FIRSTS.filter((f) => !taken.includes(f.key) || f.key === d.first_key).map((f) => (
                <button key={f.key} type="button" onClick={() => set({ first_key: f.key })} className={`flex flex-col items-center gap-1 rounded-xl p-2 text-center text-[11px] leading-tight ${d.first_key === f.key ? "font-semibold text-white" : "bg-surface-raised"}`} style={d.first_key === f.key ? { background: tokenColour("chart-6") } : undefined}>
                  <span className="text-2xl">{f.emoji}</span>{f.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {d.kind === "growth" && (
          <div className="grid grid-cols-3 gap-3">
            <Lbl label="Weight (kg)"><input className={inputCls} inputMode="decimal" value={kg} onChange={(e) => setKg(e.target.value)} placeholder="5.4" /></Lbl>
            <Lbl label="Length (cm)"><input className={inputCls} inputMode="decimal" value={cm} onChange={(e) => setCm(e.target.value)} placeholder="59" /></Lbl>
            <Lbl label="Head (cm)"><input className={inputCls} inputMode="decimal" value={head} onChange={(e) => setHead(e.target.value)} placeholder="39" /></Lbl>
          </div>
        )}

        {(d.kind === "moment" || d.kind === "first") && (
          <div>
            <span className="text-sm font-medium text-ink-muted">Photos</span>
            <input ref={pick} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { const add = Array.from(e.target.files ?? []); const from = items.length; setItems([...items, ...add.map((f) => ({ orig: f, file: f, edited: false }))]); setQueue(add.map((_, i) => from + i)); setBatch(add.length); e.target.value = ""; }} />
            <div className="mt-2 grid grid-cols-4 gap-2">
              {photos.map((p) => (
                <div key={p.id} className="relative"><PhotoImgThumb id={p.id} /><button type="button" onClick={() => archive.mutate({ photoId: p.id })} aria-label="Remove photo" className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"><X className="h-3 w-3" /></button></div>
              ))}
              {previews.map((u, i) => (
                <div key={u} className="relative">
                  <button type="button" onClick={() => { setQueue([i]); setBatch(1); }} className="block w-full" aria-label="Edit photo"><img src={u} alt="" className="aspect-square w-full rounded-lg object-cover" /></button>
                  <button type="button" onClick={() => { setItems(items.filter((_, j) => j !== i)); setQueue([]); }} aria-label="Remove photo" className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"><X className="h-3 w-3" /></button>
                  <span className="pointer-events-none absolute bottom-1 left-1 flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] text-white"><Wand2 className="h-3 w-3" />{items[i]?.edited ? "Edited" : "Edit"}</span>
                </div>
              ))}
              <button type="button" onClick={() => pick.current?.click()} className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-line text-xs text-ink-muted hover:text-ink"><ImagePlus className="h-6 w-6" />Add</button>
            </div>
          </div>
        )}

        {d.kind !== "growth" && (
          <>
            <Lbl label={d.kind === "letter" ? "What it's about (optional)" : "Caption"}>
              <input className={`${inputCls} font-hand text-2xl`} value={d.title ?? ""} onChange={(e) => set({ title: e.target.value })} placeholder={d.kind === "letter" ? "Your first Christmas" : d.kind === "first" ? "She did it at Nanna's!" : "Blowing raspberries at the dog"} />
            </Lbl>
            <Lbl label={d.kind === "letter" ? `Dear ${kid.display_name},` : "The story (optional)"}>
              <textarea className={`${inputCls} ${d.kind === "letter" ? "min-h-[260px]" : "min-h-[100px]"} font-hand text-[22px] leading-8`} value={d.body ?? ""} onChange={(e) => set({ body: e.target.value })} />
            </Lbl>
          </>
        )}
        {d.kind === "growth" && <Lbl label="Note (optional)"><input className={inputCls} value={d.body ?? ""} onChange={(e) => set({ body: e.target.value })} placeholder="Child health nurse check" /></Lbl>}

        {d.kind === "moment" && (
          <div>
            <span className="text-sm font-medium text-ink-muted">Her mood</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {MOODS.map((m) => (
                <button key={m.key} type="button" onClick={() => set({ mood: d.mood === m.key ? null : m.key })} title={m.label}
                  className={`flex h-12 w-12 items-center justify-center rounded-full text-2xl transition ${d.mood === m.key ? "scale-110 ring-2" : "bg-surface-raised opacity-70 hover:opacity-100"}`}
                  style={d.mood === m.key ? { background: tokenColour(kid.colour, 0.15), ["--tw-ring-color" as string]: c } : undefined}>{m.emoji}</button>
              ))}
            </div>
          </div>
        )}

        <ErrorText>{err}</ErrorText>
        <div className="flex gap-2">
          <button type="button" onClick={submit} disabled={save.isPending} className="flex flex-1 items-center justify-center gap-2 rounded-full py-3 font-semibold text-white disabled:opacity-60" style={{ background: c }}>
            {save.isPending ? <><Loader2 className="h-4 w-4 animate-spin" />Saving{files.length ? " photos" : ""}…</> : d.id ? <><Pencil className="h-4 w-4" />Save</> : d.kind === "letter" ? <><Mail className="h-4 w-4" />Seal it</> : d.kind === "first" ? <><Star className="h-4 w-4" />Add to her book</> : <><Heart className="h-4 w-4" />Keep it</>}
          </button>
          {d.id && (sure
            ? <button type="button" onClick={() => archive.mutate({ entryId: d.id }, { onSuccess: onClose })} className="rounded-full bg-bad/10 px-4 py-3 text-sm font-semibold text-bad">Yes, remove</button>
            : <button type="button" onClick={() => setSure(true)} aria-label="Remove" className="rounded-full bg-surface-raised px-4 py-3 text-bad"><Trash2 className="h-4 w-4" /></button>)}
        </div>
      </div>
    </Sheet>
    </>
  );
}

function PhotoImgThumb({ id }: { id: string }) { return <PhotoImg id={id} className="aspect-square w-full rounded-lg object-cover" />; }
